import { useCallback, useEffect, useState } from "react";
import { supabaseClient } from "../../utility/supabaseClient";

export type DashboardStats = {
  totalMovies: number;
  publishedMovies: number;
  draftMovies: number;
  scheduledMovies: number;
  totalCategories: number;
  lastSyncISO: string;
  // 会员统计
  membership: {
    totalUsers: number;
    freeUsers: number;
    paidUsers: number;
    activeSubscriptions: number;
    totalRevenueCents: number;
    totalRedemptions: number;
    activeCodes: number;
    expiredCodes: number;
  };
  // 用户增长趋势
  userGrowth: Array<{
    date: string;
    newUsersCount: number;
    cumulativeTotal: number;
  }>;
  // 订阅分布
  subscriptionDistribution: Array<{
    tierName: string;
    tierDisplayName: string;
    userCount: number;
    percentage: number;
  }>;
  // 热门兑换码
  popularCodes: Array<{
    batchId: string;
    tierName: string;
    tierDisplayName: string;
    totalCodes: number;
    usedCodes: number;
    usageRate: number;
    totalRevenueCents: number;
  }>;
  // 热门影片
  topMovies: Array<{
    id: string;
    title: string;
    viewCount: number;
    popularity: number;
    voteAverage: number;
    status?: string | null;
    releaseDate?: string | null;
  }>;
};

const PUBLISHED_STATUSES = ["released", "published"];
const SCHEDULED_STATUSES = ["scheduled"];
const FALLBACK_FREE_TIER_NAME = "free";

const defaultStats: DashboardStats = {
  totalMovies: 0,
  publishedMovies: 0,
  draftMovies: 0,
  scheduledMovies: 0,
  totalCategories: 0,
  lastSyncISO: "",
  membership: {
    totalUsers: 0,
    freeUsers: 0,
    paidUsers: 0,
    activeSubscriptions: 0,
    totalRevenueCents: 0,
    totalRedemptions: 0,
    activeCodes: 0,
    expiredCodes: 0,
  },
  userGrowth: [],
  subscriptionDistribution: [],
  popularCodes: [],
  topMovies: [],
};

const countMovies = async (statuses?: string | string[]) => {
  let query = supabaseClient
    .from("movies")
    .select("id", { count: "exact", head: true });

  if (Array.isArray(statuses) && statuses.length > 0) {
    query = query.in("status", statuses);
  } else if (typeof statuses === "string" && statuses) {
    query = query.eq("status", statuses);
  }

  const { count, error } = await query;

  if (error) {
    throw error;
  }

  return count ?? 0;
};

const countCategories = async () => {
  const { count, error } = await supabaseClient
    .from("categories")
    .select("id", { count: "exact", head: true });

  if (error) {
    throw error;
  }

  return count ?? 0;
};

const fetchTopMovies = async () => {
  const { data, error } = await supabaseClient
    .from("movies")
    .select("id, title, view_count, popularity, vote_average, status, release_date")
    .in("status", PUBLISHED_STATUSES)
    .order("view_count", { ascending: false })
    .order("popularity", { ascending: false })
    .limit(8);

  if (error) {
    console.error("获取热门影片失败:", error);
    return [];
  }

  return (
    data?.map((movie) => ({
      id: movie.id,
      title: movie.title,
      viewCount: movie.view_count ?? 0,
      popularity: movie.popularity ?? 0,
      voteAverage: movie.vote_average ?? 0,
      status: movie.status,
      releaseDate: movie.release_date,
    })) ?? []
  );
};

const fetchMembershipOverviewFallback = async () => {
  const [{ count: totalUsers }, { data: membershipRows }, { data: historyRows }] = await Promise.all([
    supabaseClient.from("user_memberships").select("id", { count: "exact", head: true }),
    supabaseClient
      .from("user_memberships")
      .select("status, membership_tiers(name, display_name)"),
    supabaseClient.from("redemption_history").select("id, tier_id, membership_tiers(price_cents)"),
  ]);

  const activeSubscriptions = membershipRows?.filter((row: any) => row.status === "active").length ?? 0;

  const freeUsers =
    membershipRows?.filter((row: any) => {
      const tierName = row.membership_tiers?.name;
      return tierName === FALLBACK_FREE_TIER_NAME;
    }).length ?? 0;
  const paidUsers = (membershipRows?.length ?? 0) - freeUsers;

  const totalRevenueCents =
    historyRows?.reduce((sum, row: any) => sum + (row.membership_tiers?.price_cents ?? 0), 0) ?? 0;

  const totalRedemptions = historyRows?.length ?? 0;

  const [{ count: activeCodes }, { count: expiredCodes }] = await Promise.all([
    supabaseClient
      .from("redemption_codes")
      .select("id", { count: "exact", head: true })
      .eq("status", "active")
      .is("valid_until", null),
    supabaseClient
      .from("redemption_codes")
      .select("id", { count: "exact", head: true })
      .lt("valid_until", new Date().toISOString()),
  ]);

  return {
    totalUsers: totalUsers ?? 0,
    freeUsers,
    paidUsers,
    activeSubscriptions,
    totalRevenueCents,
    totalRedemptions,
    activeCodes: activeCodes ?? 0,
    expiredCodes: expiredCodes ?? 0,
  };
};

const fetchSubscriptionDistributionFallback = async () => {
  const { data, error } = await supabaseClient
    .from("user_memberships")
    .select("status, membership_tiers(name, display_name)")
    .eq("status", "active");

  if (error) {
    throw error;
  }

  const grouped = (data ?? []).reduce<Record<string, { count: number; displayName: string }>>(
    (acc, row: any) => {
      const key = row.membership_tiers?.name || "未知";
      const displayName = row.membership_tiers?.display_name ?? key;
      acc[key] = acc[key]
        ? { count: acc[key].count + 1, displayName }
        : { count: 1, displayName };
      return acc;
    },
    {},
  );

  const total = Object.values(grouped).reduce((sum, item) => sum + item.count, 0);

  return Object.entries(grouped).map(([tierName, item]) => ({
    tierName,
    tierDisplayName: item.displayName,
    userCount: item.count,
    percentage: total > 0 ? Number(((item.count / total) * 100).toFixed(2)) : 0,
  }));
};

const fetchPopularCodesFallback = async () => {
  const { data, error } = await supabaseClient
    .from("redemption_codes")
    .select("batch_id, tier_id, current_uses, max_uses, membership_tiers(name, display_name, price_cents)");

  if (error) {
    throw error;
  }

  const grouped = (data ?? []).reduce<Record<string, any>>((acc, row: any) => {
    if (!row.batch_id) {
      return acc;
    }
    const key = row.batch_id;
    const tierName = row.membership_tiers?.name ?? "未知";
    const tierDisplayName = row.membership_tiers?.display_name ?? tierName;
    const priceCents = row.membership_tiers?.price_cents ?? 0;
    const totalCodes = (acc[key]?.totalCodes ?? 0) + 1;
    const usedCodes = (acc[key]?.usedCodes ?? 0) + (row.current_uses ?? 0);

    acc[key] = {
      batchId: key,
      tierName,
      tierDisplayName,
      totalCodes,
      usedCodes,
      totalRevenueCents: usedCodes * priceCents,
    };
    return acc;
  }, {});

  return Object.values(grouped).map((item: any) => ({
    ...item,
    usageRate:
      item.totalCodes > 0 ? Number(((item.usedCodes / item.totalCodes) * 100).toFixed(2)) : 0,
  }));
};

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const [
        totalMovies,
        publishedMovies,
        draftMovies,
        scheduledMovies,
        totalCategories,
        topMovies,
      ] = await Promise.all([
        countMovies(),
        countMovies(PUBLISHED_STATUSES),
        countMovies("draft"),
        countMovies(SCHEDULED_STATUSES),
        countCategories(),
        fetchTopMovies(),
      ]);

      const [
        { data: membershipData, error: membershipError },
        { data: growthData, error: growthError },
        { data: distributionData, error: distributionError },
        { data: popularCodesData, error: popularCodesError },
      ] = await Promise.all([
        supabaseClient.rpc("get_membership_overview"),
        supabaseClient.rpc("get_user_growth_trend", { p_days: 30 }),
        supabaseClient.rpc("get_subscription_distribution"),
        supabaseClient.rpc("get_popular_redemption_codes", { p_limit: 10 }),
      ]);

      let membershipOverview =
        membershipData?.[0] || {
          total_users: 0,
          free_users: 0,
          paid_users: 0,
          active_subscriptions: 0,
          total_revenue_cents: 0,
          total_redemptions: 0,
          active_codes: 0,
          expired_codes: 0,
        };

      if (membershipError) {
        console.error("获取会员统计失败，使用表聚合回退:", membershipError);
        membershipOverview = await fetchMembershipOverviewFallback();
      }

      if (growthError) {
        console.error("获取用户增长趋势失败:", growthError);
      }

      let subscriptionDistribution =
        distributionData?.map((row: any) => ({
          tierName: row.tier_name,
          tierDisplayName: row.tier_display_name,
          userCount: row.user_count,
          percentage: row.percentage,
        })) || [];

      if (distributionError) {
        console.error("获取订阅分布失败，使用表聚合回退:", distributionError);
        subscriptionDistribution = await fetchSubscriptionDistributionFallback();
      }

      let popularCodes =
        popularCodesData?.map((row: any) => ({
          batchId: row.batch_id,
          tierName: row.tier_name,
          tierDisplayName: row.tier_display_name,
          totalCodes: row.total_codes,
          usedCodes: row.used_codes,
          usageRate: row.usage_rate,
          totalRevenueCents: row.total_revenue_cents,
        })) || [];

      if (popularCodesError) {
        console.error("获取热门兑换码失败，使用表聚合回退:", popularCodesError);
        popularCodes = await fetchPopularCodesFallback();
      }

      setStats({
        totalMovies,
        publishedMovies,
        draftMovies,
        scheduledMovies,
        totalCategories,
        lastSyncISO: new Date().toISOString(),
        membership: {
          totalUsers: membershipOverview.total_users || 0,
          freeUsers: membershipOverview.free_users || 0,
          paidUsers: membershipOverview.paid_users || 0,
          activeSubscriptions: membershipOverview.active_subscriptions || 0,
          totalRevenueCents: membershipOverview.total_revenue_cents || 0,
          totalRedemptions: membershipOverview.total_redemptions || 0,
          activeCodes: membershipOverview.active_codes || 0,
          expiredCodes: membershipOverview.expired_codes || 0,
        },
        userGrowth:
          growthData?.map((row: any) => ({
            date: row.date,
            newUsersCount: row.new_users_count,
            cumulativeTotal: row.cumulative_total,
          })) || [],
        subscriptionDistribution,
        popularCodes,
        topMovies,
      });
    } catch (error) {
      console.error("加载仪表盘统计数据失败", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60 * 1000);

    return () => clearInterval(timer);
  }, [refresh]);

  return { stats, loading, refresh };
};
